import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { authService } from '../services/authService'

const pageStyles: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
}

const containerStyles: React.CSSProperties = {
  width: '100%',
  maxWidth: '400px',
}

const cardStyles: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '0.75rem',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  padding: '2rem',
}

const headerStyles: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: '2rem',
}

const titleStyles: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#111827',
  marginBottom: '0.5rem',
}

const formStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
}

const inputGroupStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
}

const labelStyles: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
}

const inputStyles: React.CSSProperties = {
  padding: '0.75rem',
  borderRadius: '0.5rem',
  border: '1px solid #d1d5db',
  fontSize: '1rem',
}

const buttonStyles: React.CSSProperties = {
  backgroundColor: '#2563eb',
  color: 'white',
  padding: '0.75rem 1rem',
  borderRadius: '0.5rem',
  border: 'none',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
}

const errorStyles: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '0.875rem',
}

const successStyles: React.CSSProperties = {
  color: '#065f46',
  backgroundColor: '#ecfdf5',
  border: '1px solid #a7f3d0',
  borderRadius: '0.5rem',
  padding: '0.75rem',
  fontSize: '0.875rem',
  lineHeight: 1.6,
}

const linkStyles: React.CSSProperties = {
  textAlign: 'center',
  marginTop: '1rem',
  fontSize: '0.875rem',
  color: '#6b7280',
}

const linkAnchorStyles: React.CSSProperties = {
  color: '#2563eb',
  textDecoration: 'none',
  fontWeight: '500',
}

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const navigate = useNavigate()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')

    if (!token) {
      setError('リンクが正しくありません。メールのリンクから開き直してください。')
      return
    }
    if (newPassword.length < 8) {
      setError('パスワードは8文字以上にしてください。')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('パスワードが一致しません。')
      return
    }

    setIsLoading(true)
    try {
      const result = await authService.confirmPasswordReset(token, newPassword)
      setMessage(result.message)
      setTimeout(() => navigate('/login'), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'パスワードの更新に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={pageStyles}>
      <div style={containerStyles}>
        <div style={cardStyles}>
          <div style={headerStyles}>
            <h1 style={titleStyles}>新しいパスワードを設定</h1>
          </div>

          {message ? (
            <div style={successStyles} role="status">
              {message} ログイン画面に移動します…
            </div>
          ) : (
            <form style={formStyles} onSubmit={handleSubmit}>
              {!token && (
                <div role="alert" style={errorStyles}>
                  リンクにトークンが含まれていません。メールのリンクから開き直してください。
                </div>
              )}

              <div style={inputGroupStyles}>
                <label htmlFor="new-password" style={labelStyles}>
                  新しいパスワード
                </label>
                <input
                  type="password"
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={inputStyles}
                  placeholder="8文字以上"
                  autoComplete="new-password"
                  required
                />
              </div>

              <div style={inputGroupStyles}>
                <label htmlFor="confirm-password" style={labelStyles}>
                  新しいパスワード（確認）
                </label>
                <input
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={inputStyles}
                  autoComplete="new-password"
                  required
                />
              </div>

              {error && (
                <div role="alert" aria-live="assertive" style={errorStyles}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                style={{ ...buttonStyles, ...(isLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
              >
                {isLoading ? '更新中...' : 'パスワードを更新'}
              </button>
            </form>
          )}

          <div style={linkStyles}>
            <Link to="/login" style={linkAnchorStyles}>
              ログインに戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
